<template>
    <div class="max-w-(--breakpoint-sm) flex flex-col gap-4">
        <page-header>Profile</page-header>

        <vx-expandable-card>
            <template v-slot:content>
                <div class="flex flex-col gap-6">
                    <vx-alert v-if="success" :closeable="false" color="success" shadow> Updated successfully </vx-alert>

                    <div>
                        <vx-label size="md"> Name </vx-label>

                        <vx-input
                            size="lg"
                            name="name"
                            v-model="user.name"
                            :error="displayFirstError(validate.name.$errors)"
                        ></vx-input>
                    </div>

                    <div>
                        <vx-label size="md"> Email </vx-label>

                        <vx-input
                            size="lg"
                            name="email"
                            v-model="user.email"
                            :error="displayFirstError(validate.email.$errors)"
                        ></vx-input>
                    </div>

                    <div>
                        <vx-label size="md">Time Zone</vx-label>

                        <vx-selectable
                            size="lg"
                            name="profile_time_zone"
                            v-model="user.time_zone"
                            data-test="profile-time-zone"
                            :clearable="false"
                            :error="displayFirstError(validate.time_zone.$errors)"
                            :options="timeZonesList"
                            :reduce="(option) => option.value"
                        >
                        </vx-selectable>
                    </div>

                    <vx-button data-test="submit" block size="lg" @click.prevent="submit" :loading="submitting">
                        Update
                    </vx-button>
                </div>
            </template>
        </vx-expandable-card>
    </div>
</template>

<script setup>
import PageHeader from '../../general/PageHeader.vue';
import { VxAlert, VxButton, VxExpandableCard, VxInput, VxLabel, VxSelectable } from '@voxie/frontend-components';
import { computed, ref } from 'vue';
import { displayFirstError, validateRules } from '~/utils/validation';
import { timeZoneOptions } from '~/utils/timeZones';
import { meClient } from '~/services';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import useVuelidate from '@vuelidate/core';
import { email, maxLength, required } from '@vuelidate/validators';

const user = defineModel('user', {
    type: Object,
    required: true,
});

const timeZonesList = computed(() =>
    timeZoneOptions(user.value.time_zone).map((timeZone) => ({
        value: timeZone,
        label: timeZone.split('_').join(' '),
    }))
);

const submitting = ref(false);
const success = ref(false);
const { $externalResults, catchError } = useFormRequestHandler();

const validate = useVuelidate(
    {
        name: { required, maxLength: maxLength(191) },
        email: { required, email, maxLength: maxLength(191) },
        time_zone: { required, maxLength: maxLength(30) },
    },
    user,
    { $externalResults }
);

const submit = async () => {
    if (submitting.value) {
        return;
    }

    const isValid = await validateRules(validate);

    if (!isValid) {
        return;
    }

    success.value = false;
    submitting.value = true;

    try {
        await meClient.update({
            name: user.value.name,
            email: user.value.email,
            time_zone: user.value.time_zone,
        });
        success.value = true;
        validate.value.$reset();
    } catch (e) {
        catchError(e);
    } finally {
        submitting.value = false;
    }
};
</script>
